import { Lock, Scale } from "lucide-react";
import { Link } from "react-router";
import { formatAmountWithCurrency, formatDate } from "~/lib/format";
import type { GovernanceResponse } from "~/types/corporate";
import { DataRow } from "./overview/card";

const LEGAL_FORM_LABELS: Record<string, string> = {
	sro: "Společnost s ručením omezeným",
	as: "Akciová společnost",
	other: "Jiná právní forma",
};

const GOVERNANCE_MODEL_LABELS: Record<string, string> = {
	dualistic: "Dualistická struktura",
	monistic: "Monistická struktura",
};

/** Ano / Ne / — chip for the boolean governance flags. */
function BoolChip({ value }: { value: boolean | null }) {
	if (value === true) {
		return <span className="badge badge-soft badge-success badge-sm shrink-0">Ano</span>;
	}
	if (value === false) {
		return <span className="badge badge-soft badge-ghost badge-sm shrink-0">Ne</span>;
	}
	return <span className="text-sm text-muted-foreground shrink-0">—</span>;
}

function FlagRow({
	label,
	value,
	note,
}: {
	label: string;
	value: boolean | null;
	note?: string | null;
}) {
	return (
		<div className="flex flex-col gap-1 rounded-md border border-gray-100 px-3 py-2.5">
			<div className="flex items-center justify-between gap-3">
				<p className="text-sm">{label}</p>
				<BoolChip value={value} />
			</div>
			{note && <p className="text-xs text-muted-foreground">{note}</p>}
		</div>
	);
}

/**
 * "Správa společnosti" — governance profile extracted from the articles of
 * association (stanovy / společenská smlouva). Renders nothing when no
 * governance data exists.
 */
export function GovernanceSection({ response }: { response: GovernanceResponse | null }) {
	if (!response || !response.governance) return null;
	const { governance, locked } = response;

	const legalForm = governance.legalForm ? LEGAL_FORM_LABELS[governance.legalForm] : null;
	const governanceModel = governance.governanceModel
		? (GOVERNANCE_MODEL_LABELS[governance.governanceModel] ?? governance.governanceModel)
		: null;

	return (
		<div className="card-surface rounded-xl p-6">
			<div className="flex items-center gap-2 mb-4">
				<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50">
					<Scale className="h-4 w-4 text-primary-600" />
				</div>
				<h2 className="text-lg font-semibold">Správa společnosti</h2>
				{governance.sourceDate && (
					<span className="text-xs text-muted-foreground ml-auto">
						dle stanov z {formatDate(governance.sourceDate)}
					</span>
				)}
			</div>

			<dl className="flex flex-col gap-3 text-sm mb-4">
				{legalForm && <DataRow label="Právní forma" value={legalForm} />}
				{governanceModel && <DataRow label="Struktura řízení" value={governanceModel} />}
				{governance.registeredCapitalAmount != null && (
					<DataRow
						label="Základní kapitál"
						value={formatAmountWithCurrency(
							governance.registeredCapitalAmount,
							governance.registeredCapitalCurrency,
						)}
						mono
					/>
				)}
				{governance.wayOfActing && (
					<DataRow label="Způsob jednání" value={governance.wayOfActing} />
				)}
			</dl>

			<div className="flex flex-col gap-2">
				<FlagRow
					label="Omezení převoditelnosti podílů"
					value={governance.hasTransferRestrictions}
					note={governance.transferRestrictionsNote}
				/>
				<FlagRow
					label="Předkupní/přednostní práva"
					value={governance.hasPreemptiveRights}
					note={governance.preemptiveRightsNote}
				/>
				<FlagRow
					label="Zvláštní pravidla rozdělení zisku"
					value={governance.hasProfitDistributionRules}
					note={governance.profitDistributionNote}
				/>
				<FlagRow
					label="Zákaz konkurence"
					value={governance.hasNonCompete}
					note={governance.nonCompeteNote}
				/>
				<FlagRow label="Rozhodování per rollam" value={governance.perRollamAllowed} />
			</div>

			{!locked &&
				(governance.supermajorityNote ||
					governance.quorumNote ||
					governance.liquidationPreferenceNote) && (
					<div className="mt-3 flex flex-col gap-1.5 rounded-md border border-gray-100 px-3 py-2.5">
						<p className="text-sm font-medium">Další ustanovení</p>
						{governance.supermajorityNote && (
							<p className="text-xs text-muted-foreground">
								Kvalifikovaná většina: {governance.supermajorityNote}
							</p>
						)}
						{governance.quorumNote && (
							<p className="text-xs text-muted-foreground">
								Usnášeníschopnost: {governance.quorumNote}
							</p>
						)}
						{governance.liquidationPreferenceNote && (
							<p className="text-xs text-muted-foreground">
								Likvidační zůstatek: {governance.liquidationPreferenceNote}
							</p>
						)}
					</div>
				)}

			{locked && (
				<div className="mt-4 rounded-lg border border-dashed border-primary-200 bg-primary-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
					<div className="flex items-start gap-3">
						<Lock className="h-4 w-4 text-primary-600 mt-0.5 shrink-0" />
						<div className="flex flex-col">
							<p className="text-sm font-medium text-foreground">
								Detaily ustanovení v plánu Premium
							</p>
							<p className="text-xs text-muted-foreground mt-0.5">
								Plné znění ustanovení ze stanov — způsob jednání, druhy podílů a poznámky k
								jednotlivým pravidlům — odemkne plán Premium.
							</p>
						</div>
					</div>
					<Link to="/cenik" className="btn btn-sm btn-primary shrink-0">
						Upgradovat
					</Link>
				</div>
			)}
		</div>
	);
}
